import { Button, Modal, Progress, Typography } from "antd"
import React, { useEffect, useState } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import { fetchBranches, fetchSetupStatus, selectSetupStatus } from "../redux/branches.slice.ts"
import { AddBranchModal } from "./AddBranchModal.tsx"

const { Text } = Typography

/**
 * The resumable "Set up your branches" flow a GCC Owner reaches from the
 * Home CTA (`ContinueBranchSetupBanner`, via `BranchSetupFlow`) or from
 * Settings → Branches' own reminder. Deliberately holds no wizard state of
 * its own beyond local UI toggles — every re-open re-derives where it is
 * from the live `setupStatus`/branch list, so "Finish later" has nothing to
 * persist and a later resume (reload, new login, another tab) is correct
 * by construction.
 *
 * Step 1 (re-)configures the already-created original branch — a genuinely
 * missing capability before this (`AddBranchModal`'s new edit mode is the
 * fix); it is offered every time the wizard opens rather than remembered as
 * "done," since re-submitting unchanged values is a harmless no-op and
 * tracking that separately would be exactly the extra state this flow is
 * designed not to need. Step 2+ adds the remaining planned branches one at
 * a time via `AddBranchModal`'s existing create mode.
 */
export function BranchSetupWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectSetupStatus)
  const managedBranches = useAppSelector((state) => state.branches.managedBranches)
  const [originalConfigured, setOriginalConfigured] = useState(false)
  const [editOriginalOpen, setEditOriginalOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    setOriginalConfigured(false)
    dispatch(fetchBranches({ include_archived: true }))
    dispatch(fetchSetupStatus())
  }, [open, dispatch])

  const refreshStatus = () => {
    dispatch(fetchSetupStatus())
  }

  const originalBranch =
    managedBranches.find((b) => b.is_default_branch) ?? managedBranches[0] ?? null

  const done = status?.done ?? false
  const percent = status && status.target > 0 ? Math.min(100, (status.created / status.target) * 100) : 0

  return (
    <>
      <Modal
        title="Set up your branches"
        open={open}
        onCancel={onClose}
        footer={
          <Button onClick={onClose}>{done ? "Close" : "Finish later"}</Button>
        }
        destroyOnClose
      >
        <Progress percent={percent} showInfo={false} className="mb-2" />
        <Text className="block mb-4">
          {status ? `${status.created} of ${status.target} branches configured` : "Loading…"}
        </Text>

        {done ? (
          <Text type="success">All planned branches are set up.</Text>
        ) : !originalBranch ? (
          <Text type="secondary">Loading your branches…</Text>
        ) : !originalConfigured ? (
          <div>
            <Text className="block mb-2">
              Start by confirming {originalBranch.name}&apos;s details — name, time zone,
              manager, and anything else specific to this branch.
            </Text>
            <div className="flex gap-2">
              <Button type="primary" onClick={() => setEditOriginalOpen(true)}>
                Configure {originalBranch.name}
              </Button>
              <Button type="link" onClick={() => setOriginalConfigured(true)}>
                Skip for now
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Text className="block mb-2">
              Add your next branch whenever you&apos;re ready.
            </Text>
            <Button type="primary" onClick={() => setAddOpen(true)}>
              Add branch
            </Button>
          </div>
        )}
      </Modal>

      {originalBranch && (
        <AddBranchModal
          open={editOriginalOpen}
          onClose={() => setEditOriginalOpen(false)}
          branchId={originalBranch.id}
          onSaved={() => {
            setEditOriginalOpen(false)
            setOriginalConfigured(true)
            refreshStatus()
          }}
        />
      )}

      <AddBranchModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={() => {
          setAddOpen(false)
          refreshStatus()
        }}
      />
    </>
  )
}
